/**
 * Shared Notification Service
 * 
 * Toast notification system using events.
 * Use with a notification provider component.
 * 
 * @example
 * ```tsx
 * import { notify } from '@/shared/services';
 * 
 * notify.success('User created successfully');
 * notify.error('Failed to save changes');
 * notify.warning('This action cannot be undone');
 * notify.info('Processing your request...');
 * ```
 */

// =============================================================================
// TYPES
// =============================================================================

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export type NotificationVariant = 'default' | 'realtime';

export interface Notification {
  id: string;
  type: NotificationType;
  title?: string;
  message: string;
  duration?: number;
  variant?: NotificationVariant;
  appName?: string;
  appLogoUrl?: string;
  onClickNavigate?: () => void;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export interface NotificationOptions {
  title?: string;
  duration?: number;
  variant?: NotificationVariant;
  appName?: string;
  appLogoUrl?: string;
  onClickNavigate?: () => void;
  action?: {
    label: string;
    onClick: () => void;
  };
}

// =============================================================================
// EVENT SYSTEM
// =============================================================================

type NotificationCallback = (notification: Notification) => void;
type DismissCallback = (id: string) => void;

const listeners = {
  notification: new Set<NotificationCallback>(),
  dismiss: new Set<DismissCallback>(),
  clear: new Set<() => void>(),
};

/**
 * Subscribe to notification events
 */
export function onNotification(callback: NotificationCallback): () => void {
  listeners.notification.add(callback);
  return () => listeners.notification.delete(callback);
}

/**
 * Subscribe to dismiss events
 */
export function onDismiss(callback: DismissCallback): () => void {
  listeners.dismiss.add(callback);
  return () => listeners.dismiss.delete(callback);
}

/**
 * Subscribe to clear events
 */
export function onClear(callback: () => void): () => void {
  listeners.clear.add(callback);
  return () => listeners.clear.delete(callback);
}

// =============================================================================
// NOTIFICATION SERVICE
// =============================================================================

let idCounter = 0;

function generateId(): string {
  return `notification_${++idCounter}_${Date.now()}`;
}

function emit(notification: Notification): string {
  listeners.notification.forEach((callback) => callback(notification));
  return notification.id;
}

/**
 * Notification service
 */
export const notify = {
  /**
   * Show a success notification
   */
  success(message: string, options?: NotificationOptions): string {
    return emit({
      id: generateId(),
      type: 'success',
      message,
      duration: 4000,
      ...options,
    });
  },

  /**
   * Show an error notification
   */
  error(message: string, options?: NotificationOptions): string {
    return emit({
      id: generateId(),
      type: 'error',
      message,
      duration: 6000, // Errors stay longer
      ...options,
    });
  },

  /**
   * Show a warning notification
   */
  warning(message: string, options?: NotificationOptions): string {
    return emit({
      id: generateId(),
      type: 'warning',
      message,
      duration: 5000,
      ...options,
    });
  },

  /**
   * Show an info notification
   */
  info(message: string, options?: NotificationOptions): string {
    return emit({
      id: generateId(),
      type: 'info',
      message,
      duration: 4000,
      ...options,
    });
  },

  /**
   * Show a custom notification
   */
  custom(notification: Omit<Notification, 'id'>): string {
    return emit({
      id: generateId(),
      ...notification,
    });
  },

  /**
   * Dismiss a specific notification
   */
  dismiss(id: string): void {
    listeners.dismiss.forEach((callback) => callback(id));
  },

  /**
   * Clear all notifications
   */
  clear(): void {
    listeners.clear.forEach((callback) => callback());
  },

  /**
   * Show loading and return a function to update/dismiss
   */
  loading(message: string): {
    success: (message: string) => void;
    error: (message: string) => void;
    dismiss: () => void;
  } {
    const id = emit({
      id: generateId(),
      type: 'info',
      message,
      duration: 0, // Don't auto-dismiss
    });

    return {
      success: (msg: string) => {
        this.dismiss(id);
        this.success(msg);
      },
      error: (msg: string) => {
        this.dismiss(id);
        this.error(msg);
      },
      dismiss: () => {
        this.dismiss(id);
      },
    };
  },
};

export default notify;


